/**
 * 仪表板的数据请求（TanStack Query），接口、参数与字段映射与旧版 DashboardView.vue 一致
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { statsApi } from '@/api/services/stats'
import type { NamedValue, TrendSeries } from '@/components/charts/presets'

export const DASHBOARD_KEY = ['admin', 'dashboard'] as const

// 实时动态每 30 秒刷新一次（与旧版一致）
const REALTIME_REFRESH_MS = 30000

export interface OverviewStats {
  totalUsers: number
  todayNewUsers: number
  growthRate: number
  activeUsers: number
  totalLearningSeconds: number | null
}

export interface TrendData {
  dates: string[]
  series: TrendSeries[]
}

export interface RecentActivity {
  user: string
  type: 'registration' | 'learning'
  time: string
  detail: string
}

export interface RealtimeData {
  todayActiveUsers: number
  recentActivities: RecentActivity[]
}

export function useOverviewStats() {
  return useQuery({
    queryKey: [...DASHBOARD_KEY, 'overview'],
    queryFn: async (): Promise<OverviewStats> => {
      const data = (await statsApi.getOverview()).data.data ?? {}
      return {
        totalUsers: data.total_users || 0,
        todayNewUsers: data.today_new_users || 0,
        growthRate: data.growth_rate || 0,
        activeUsers: data.active_users || 0,
        totalLearningSeconds: data.learning_seconds ?? null
      }
    }
  })
}

// 用户增长趋势：最近 days 个自然日的新增和活跃用户
export function useTrends(days: number) {
  return useQuery({
    queryKey: [...DASHBOARD_KEY, 'trends', days],
    queryFn: async (): Promise<TrendData> => {
      const dailyStats: any[] = (await statsApi.getTrends(days)).data.data ?? []
      return {
        dates: dailyStats.map((item) => item.date),
        series: [
          { name: '新增用户', data: dailyStats.map((item) => item.new_users) },
          { name: '活跃用户', data: dailyStats.map((item) => item.active_users) }
        ]
      }
    },
    placeholderData: keepPreviousData
  })
}

// 用户等级分布；后端返回的设备分布是写死的 0，不再展示
export function useLevelDistribution() {
  return useQuery({
    queryKey: [...DASHBOARD_KEY, 'distribution'],
    queryFn: async (): Promise<NamedValue[]> => (await statsApi.getDistribution()).data.data?.level_distribution ?? []
  })
}

export function useRealtime() {
  return useQuery({
    queryKey: [...DASHBOARD_KEY, 'realtime'],
    queryFn: async (): Promise<RealtimeData> => {
      const data = (await statsApi.getRealtime()).data.data ?? {}
      return {
        todayActiveUsers: data.today_active_users ?? 0,
        recentActivities: (data.recent_activities ?? []).map((activity: any) => ({
          user: activity.user,
          type: activity.action === '注册' ? 'registration' : 'learning',
          time: activity.details || activity.time,
          detail: activity.action || '活动'
        }))
      }
    },
    refetchInterval: REALTIME_REFRESH_MS
  })
}
